import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

/**
 * Like react-use's `useLocalStorage`, but the value is always JSON-parsed
 * on read and JSON-stringified on write. The in-memory value is kept as
 * the parsed type `T`. Any error (missing key, malformed JSON, storage
 * access errors, etc.) falls back to `initialValue`.
 */
export default function useLocalStorageJSON<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>, () => void] {
  if (!key) {
    throw new Error('useLocalstorageJSON key may not be falsy')
  }

  // Keep the latest initialValue available to callbacks without
  // forcing them to depend on (and re-create on) its identity.
  const initialValueRef = useRef(initialValue)
  initialValueRef.current = initialValue

  const readValue = useCallback((): T => {
    const raw = localStorage.getItem(key)
    if (raw === null) {
      return initialValueRef.current
    }

    try {
      return JSON.parse(raw) as T
    } catch {
      return initialValueRef.current
    }
  }, [key])

  const [state, setState] = useState<T>(readValue)

  const set: Dispatch<SetStateAction<T>> = useCallback(
    (valueOrFn) => {
      setState((prevState) => {
        const nextValue =
          typeof valueOrFn === 'function' ? (valueOrFn as (prev: T) => T)(prevState) : valueOrFn

        localStorage.setItem(key, JSON.stringify(nextValue))

        return nextValue
      })
    },
    [key]
  )

  const remove = useCallback(() => {
    localStorage.removeItem(key)
    setState(initialValueRef.current)
  }, [key])

  // Keep in sync with changes made in other tabs/windows, and with
  // changes to `key` itself.
  useEffect(() => {
    setState(readValue())

    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return
      setState(readValue())
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [key, readValue])

  return [state, set, remove]
}
